import app from 'flarum/forum/app';
// After the last keystroke, before a search goes to the server.
const SEARCH_DELAY = 300;
/**
 * A list that comes from the API a page at a time, searched, filtered and
 * sorted there - an exhibition's species (ExpoPage, ExhibitionPage), a
 * cartothèque (CartothequePage). ListEnd asks it for the next page as the
 * end of the list comes near.
 *
 * query() gives the request's filter and sort; it's read when the list
 * starts again from its first page (load()), never for the next page: a
 * search typed but not yet sent (reloadSoon()) doesn't mix with the rows
 * already shown. An answer that comes after a newer request is dropped.
 */
export default class PagedList {
  items = [];
  loading = false;
  error = false;
  hasMore = false;
  // Rows matching, every page together; null until the first answer.
  total = null;
  // One more each time the list is loaded anew - for what's kept of it.
  version = 0;
  params = {};
  request = 0;
  constructor(type, query, options) {
    this.type = type;
    this.query = query;
    this.options = options;
  }

  // The request's filter and sort, as the rows shown were loaded with.
  get currentParams() {
    return this.params;
  }

  // From its first page, as query() now asks.
  load() {
    clearTimeout(this.timeout);
    this.params = this.query();
    return this.fetch(0, this.options.pageSize);
  }

  // The next page.
  loadMore() {
    if (this.loading || this.error || !this.hasMore) return Promise.resolve();
    return this.fetch(this.items.length, this.options.pageSize, true);
  }

  // As many rows as shown, again - after a change, the list stays where it
  // was scrolled to.
  refresh() {
    return this.fetch(0, Math.min(Math.max(this.items.length, this.options.pageSize), this.options.maxPageSize));
  }

  // load() once the typing stops.
  reloadSoon() {
    clearTimeout(this.timeout);
    this.timeout = window.setTimeout(() => this.load(), SEARCH_DELAY);
  }

  // Nothing loaded, nothing on its way - another exhibition or société.
  clear() {
    clearTimeout(this.timeout);
    this.request++;
    this.items = [];
    this.loading = false;
    this.error = false;
    this.hasMore = false;
    this.total = null;
    this.meta = undefined;
  }
  fetch(offset, limit, more) {
    if (more === void 0) {
      more = false;
    }
    const request = ++this.request;
    this.loading = true;
    this.error = false;
    m.redraw();
    return app.store.find(this.type, {
      ...this.params,
      page: {
        offset,
        limit
      }
    }, undefined,
    // The page's own message and "Réessayer".
    {
      errorHandler: () => {}
    }).then(results => {
      if (request !== this.request) return;
      const meta = results.payload.meta;
      this.items = more ? [...this.items, ...results] : [...results];
      this.hasMore = !!results.payload.links?.next && results.length > 0;
      this.total = meta?.page?.total ?? this.items.length;
      if (!more) {
        this.meta = meta;
        this.version++;
        this.options.onload?.();
      }
    }).catch(() => {
      if (request === this.request) this.error = true;
    }).finally(() => {
      if (request !== this.request) return;
      this.loading = false;
      m.redraw();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/utils/PagedList', PagedList);